<script setup>
/* eslint-disable vue/no-deprecated-slot-attribute */ // Slot attribute used by SDDS
/* eslint-disable vue/no-v-html */
  import { inject, ref, computed } from 'vue';
  import { createPublicContactRequest, createPublicContactRequestV2 } from '../../../services/digitalSalesService';
  import { useMainStore } from '../../../store/index';
  import { useConfiguratorApi } from '../../../composables/configurator';

  const store = useMainStore();
  const { totalPrice } = useConfiguratorApi();
  const adobeEvent = inject('adobeEvent');
  const dsFrontendUrlVatPage = `${process.env.VUE_APP_DS_FRONTEND_URL}/register`;


  const contactType = ref('telephone');
  const customerName = ref('');
  const customerPhone = ref('');
  const customerPostalCode = ref('');
  const customerEmail = ref('');
  const companyName = ref('');
  const vatNip = ref('');
  const customerComments = ref('');
  const contactRequestProcessing = ref(false);
  const contactRequestError = ref(false);
  const contactRequestErrorFields = ref([]);

  const isVatNipRequired = computed(() => store.activeMarket?.vatNipRequiered);

  const consents = ref({
    telephone: [
      { id: 'splPhoneConsent', text: 'registerVatConsentScaniaPolska', accepted: false, required: true },
      { id: 'spfPhoneConsent', text: 'registerVatConsentScaniaFinance', accepted: false },
      { id: 'sipPhoneConsent', text: 'registerVatConsentScaniaInsurance', accepted: false },
    ],
    email: [
      { id: 'splEmailConsent', text: 'registerVatConsentScaniaPolska', accepted: false, required: true },
      { id: 'spfEmailConsent', text: 'registerVatConsentScaniaFinance', accepted: false },
      { id: 'sipEmailConsent', text: 'registerVatConsentScaniaInsurance', accepted: false },
    ],
  });

  const clickToLogin = () => window.location.href = dsFrontendUrlVatPage;

  const sendContactRequest = () => {
    // Check the contact form and just leave if not properly filled
    if (!validateContactRequest()) {
      return;
    }

    contactRequestProcessing.value = true;
    contactRequestError.value = false;
    const keys = Object.keys(consents.value);
    const mappedConsents = {};
    keys.forEach(key => {
      mappedConsents[key] = consents.value[key].map(x => ({
        id: x.id,
        accepted: x.accepted,
      }));
    });
    console.log('SENDING CONTACT REQUEST DATA:', {
      contactType: contactType.value,
      customerName: customerName.value,
      customerPhone: customerPhone.value,
      customerPostalCode: customerPostalCode.value,
      customerEmail: customerEmail.value,
      companyName: companyName.value,
      vatNip: vatNip.value,
      consents: mappedConsents,
      totalCost: totalPrice.value,
      locale: store.locale,
    });

    const payload = {
      contactType: contactType.value,
      customerName: customerName.value,
      customerPhone: customerPhone.value,
      customerPostalCode: customerPostalCode.value,
      customerEmail: customerEmail.value,
      vatNip: vatNip.value,
      consents: mappedConsents,
      selections: store.selected,
      totalCost: totalPrice.value,
      companyName: companyName.value,
      customerComments: customerComments.value,
      locale: store.locale,
    };
    adobeEvent({ event: 'sendContactRequest' });
    // createPublicContactRequest(payload)
    createPublicContactRequestV2(payload)
    .then(() => {
        console.log('CONTACT REQUEST SENT SUCCESSFULLY');
        store.contactRequested = true;
      })
      .catch((error) => {
        console.error('CONTACT REQUEST ERROR:', error);
        contactRequestError.value = true;
      })
      .finally(() => contactRequestProcessing.value = false);
  };

  const allConsentsAccepted = computed({
    get: () => Object.values(consents.value).every(typeConsents => typeConsents.every(consent => consent.accepted)),
    set: (accepted) => {
      Object.values(consents.value).forEach(typeConsents => {
        typeConsents.forEach(consent => {
          consent.accepted = accepted;
        });
      });
    },
  });

  const updateCustomerCommentsInput = (input) => customerComments.value = input.target.value;

  const isValidPhoneNumberFormat = computed(() => String(customerPhone.value).length >= 7 && !!Number(customerPhone.value));
  const regexEmail = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
  const isValidEmailAddressFormat = computed(() => regexEmail.test(customerEmail.value));
  const isValidVatNipFormat = computed(() => String(vatNip.value).length >= 10);

  const requiredConsentsAccepted = computed(() => allConsentsAccepted.value);

  const validateContactRequest = () => {
    const invalidFields = [];

    if (!customerName.value) invalidFields.push('customerNameInput');
    if (!isValidPhoneNumberFormat.value) invalidFields.push('customerPhoneNumberInput');
    if (!customerEmail.value || !isValidEmailAddressFormat.value) invalidFields.push('customerEmailInput');
    if (isVatNipRequired.value && !isValidVatNipFormat.value) invalidFields.push('customerVatNipInput');
    if (shouldPostalCodeBeRequired.value && !customerPostalCode.value) invalidFields.push('customerPostalCodeInput');

    contactRequestErrorFields.value = invalidFields;

    return invalidFields.length === 0;
  };

  const shouldPostalCodeBeRequired = computed(() => store.activeMarket?.marketId === 'de');

  const contactRequestDisabled = computed(() => {
    return !requiredConsentsAccepted.value;
  });

  const showLoginButton = store.market === 'pl';
</script>

<template>
  <div
    class="sdds-modal-backdrop show"
    :class="{ 'hide': !store.showContactRequestModal }"
  >
    <div class="sdds-modal sdds-modal-md sdds-u-pl3 sdds-u-pr3">
      <div class="sdds-modal-header">
        <h3 v-if="!store.contactRequested">
          {{ $i18n('orderRequestHeader') }}
        </h3>
        <h3 v-if="store.contactRequested">
          {{ $i18n('orderRequestConfirmationHeader') }}
        </h3>
        <span
          data-analytics="{'eventComponent':'contactRequestModal','eventType':'xmark','eventLabel':'close'}"
          class="sdds-modal-btn"
          data-dd-action-name="close"
          @click="store.showContactRequestModal = false"
        />
      </div>
      <div class="sdds-modal-body sdds-on-white-bg">
        <template v-if="!store.contactRequested">
          <p class="sdds-body-01 sdds-u-pb2">
            {{ $i18n('orderRequestText') || 'To receive a detailed offer, you can log in to MyScania account or ask your dealer to contact you.' }}
          </p>
          
          <div
            v-if="showLoginButton"
            class="sdds-u-pb3"
          >
            <button
              data-analytics="{'eventComponent':'contactRequestModal','eventType':'button','eventLabel':'log in'}"
              class="sdds-btn sdds-btn-primary sdds-btn-md"
              :disabled="contactRequestProcessing"
              data-dd-action-name="log in"
              @click="clickToLogin"
            >
              {{ $i18n('clickHereToLogin') || 'Log in' }}
            </button>
          </div>
          
          <div class="sdds-divider-light option-divider" />
          
          <div class="get-contacted-section">
            <div class="get-contacted-header contact-section-title">
              <div class="get-contacted-title">
                <span>{{ $i18n('getContactedTitle') || 'Get contacted' }}</span>
              </div>
            </div>
            
            <div class="get-contacted-content expanded">
              <div class="sdds-u-pr2 sdds-u-pb2">
                <label for="customerNameInput">
                  {{ $i18n('customerNameInput') }}<span
                    style="color: red;"
                  >*</span>
                </label>
                <input
                  id="customerNameInput"
                  v-model="customerName"
                  class="sdds-textfield-input customer-input-textfield-bg"
                  type="text"
                  autofocus
                  :disabled="contactRequestProcessing || store.contactRequested"
                  :placeholder="$i18n('customerNamePlaceholder')"
                >
              </div>
              <div class="sdds-u-pr2 sdds-u-pb2">
                <label for="emailInput">
                  {{ $i18n('customerEmailInput') }}<span style="color: red;">*</span>
                </label>
                <input
                  id="emailInput"
                  v-model="customerEmail"
                  class="sdds-textfield-input customer-input-textfield-bg"
                  type="email"
                  autofocus
                  :disabled="contactRequestProcessing || store.contactRequested"
                  :placeholder="$i18n('customerEmailPlaceholder')"
                >
              </div>
              <div class="sdds-u-pr2 sdds-u-pb2">
                <label for="phoneNumberInput">
                  {{ $i18n('customerPhoneNumberInput') }}<span style="color: red;">*</span>
                </label>
                <input
                  id="phoneNumberInput"
                  v-model="customerPhone"
                  class="sdds-textfield-input customer-input-textfield-bg"
                  type="text"
                  autofocus
                  required
                  :disabled="contactRequestProcessing || store.contactRequested"
                  :placeholder="$i18n('customerPhoneNumberPlaceholder')"
                >
              </div>
              <div
                v-if="shouldPostalCodeBeRequired"
                class="sdds-u-pr2 sdds-u-pb2"
              >
                <label for="postalCodeInput">
                  {{ $i18n('customerPostalCodeInput') }}<span style="color: red;">*</span>
                </label>
                <input
                  id="postalCodeInput"
                  v-model="customerPostalCode"
                  class="sdds-textfield-input customer-input-textfield-bg"
                  type="text"
                  autofocus
                  required
                  :disabled="contactRequestProcessing || store.contactRequested"
                  :placeholder="$i18n('customerPostalCodePlaceholder')"
                >
              </div>
              <div class="sdds-u-pr2 sdds-u-pb2">
                <label for="vatNipInput">
                  {{ $i18n('customerVatNipInput') }}<span
                    v-if="isVatNipRequired"
                    style="color: red;"
                  >*</span>
                </label>
                <input
                  id="vatNipInput"
                  v-model="vatNip"
                  class="sdds-textfield-input customer-input-textfield-bg"
                  type="text"
                  autofocus
                  :required="isVatNipRequired"
                  :disabled="contactRequestProcessing || store.contactRequested"
                  :placeholder="$i18n('customerVatNipPlaceholder')"
                >
              </div>
              <div class="sdds-u-pr2 sdds-u-pb2">
                <label for="companyName">
                  {{ $i18n('customerCompanyNameInput') }}
                </label>
                <input
                  id="companyName"
                  v-model="companyName"
                  class="sdds-textfield-input customer-input-textfield-bg"
                  type="text"
                  autofocus
                  :disabled="contactRequestProcessing || store.contactRequested"
                  :placeholder="$i18n('customerCompanyNamePlaceholder')"
                >
              </div>
              <div>
                <div class="sdds-u-pr2 sdds-u-pb2">
                  <label for="customerCommentsInput">
                    {{ $i18n('customerCommentsInput') }}
                  </label>
                  <sdds-textarea
                    :value="customerComments"
                    rows="2"
                    class="sdds-textarea-input customer-input-textarea-bg"
                    :disabled="contactRequestProcessing || store.contactRequested"
                    :placeholder="$i18n('customerCommentsPlaceholder')"
                    @input="updateCustomerCommentsInput"
                  />
                </div>
              </div>
              <div class="marketing-consent-container">
                <div class="consent-container">
                  <h4 class="sdds-headline-07">
                    {{ $i18n('deContactRequestFormConsentHeader') }}
                  </h4>
                  <div class="consent-sections">
                    <div class="consent-section">
                      <div class="sdds-checkbox-item">
                        <input
                          id="accept-all-consents"
                          v-model="allConsentsAccepted"
                          type="checkbox"
                          class="sdds-form-input"
                          :disabled="contactRequestProcessing"
                        >
                        <label
                          for="accept-all-consents"
                          class="sdds-form-label"
                        >
                          <span v-html="$i18n('deContactRequestFormConsentBody')" />
                          <span
                            class="required"
                            aria-hidden="true"
                          >*</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <h4 class="sdds-headline-07">
                  {{ $i18n('deContactRequestFormPrivacyPolicyHeader') }}
                </h4>
                <p class="sdds-detail-05 consent-footer">
                  <span v-html="$i18n('deContactRequestFormPrivacyPolicyBody')" />
                </p>

                <template v-if="store.market === 'pl'">
                  <p class="sdds-detail-05 consent-footer-links">
                    <a
                      data-analytics="{'eventComponent':'bottom','eventType':'link','eventLabel':'external link to SPL information - processing of personal data'}"
                      data-dd-action-name="external link to SPL information - processing of personal data"
                      :href="$i18n('registerVatConsentFootnoteLink1')"
                      target="_blank"
                    >{{ $i18n('registerVatConsentFootnoteLink1Text') }}
                    </a>
                    <br><br>
                    <a
                      data-analytics="{'eventComponent':'bottom','eventType':'link','eventLabel':'external link to SPL information - how SPL processes personal data (GDPR)'}"
                      data-dd-action-name="external link to SPL information - how SPL processes personal data (GDPR)"
                      :href="$i18n('registerVatConsentFootnoteLink2')"
                      target="_blank"
                    >{{ $i18n('registerVatConsentFootnoteLink2Text') }}
                    </a>
                  </p>
                </template>
              </div>
                
              <div
                v-if="contactRequestError"
                class="sdds-banner sdds-banner-error margin-bottom-24"
              >
                <div class="sdds-banner-body">
                  <h6 class="sdds-banner-header">
                    {{ $i18n('supportRequestConfirmationError') || 'There was an error submitting your request. Please try again.' }}
                  </h6>
                </div>
                <div
                  class="sdds-banner-close"
                  @click="contactRequestError=false"
                />
              </div>
              <div class="contact-button-container">
                <button
                  v-if="!store.contactRequested"
                  data-analytics="{'eventComponent':'contactRequestModal','eventType':'button','eventLabel':'send contact request'}"
                  class="sdds-btn sdds-btn-primary sdds-btn-md"
                  :disabled="contactRequestDisabled || contactRequestProcessing"
                  data-dd-action-name="send contact request"
                  @click="sendContactRequest"
                >
                  {{ $i18n('requestOrder') }}
                  <font-awesome-icon
                    v-if="contactRequestProcessing"
                    icon="spinner"
                    class="fa-spin"
                  />
                </button>
              </div>
              <div
                v-if="contactRequestErrorFields.length > 0"
                class="sdds-message sdds-message__type-error sdds-message__icon-active sdds-message__extended-active sdds-message-ongrey margin-top-16"
              >
                <svg
                  class="sdds-message-icon sdds-message-icon--error"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M10 2.50019C5.85662 2.50019 2.49775 5.85906 2.49775 10.0024C2.49775 14.1458 5.85662 17.5047 10 17.5047C14.1434 17.5047 17.5023 14.1458 17.5023 10.0024C17.5023 5.85906 14.1434 2.50019 10 2.50019ZM1.25 10.0024C1.25 5.16995 5.16751 1.25244 10 1.25244C14.8325 1.25244 18.75 5.16995 18.75 10.0024C18.75 14.8349 14.8325 18.7524 10 18.7524C5.16751 18.7524 1.25 14.8349 1.25 10.0024Z"
                    fill="#FF2340"
                  />
                  <path
                    d="M9.25175 9.044V6.25H10.7367V9.044L10.5167 11.728H9.47175L9.25175 9.044ZM9.21875 12.564H10.7807V14.06H9.21875V12.564Z"
                    fill="#FF2340"
                  />
                </svg>

                <h4 class="sdds-message-single">
                  {{ $i18n('orderRequestSubmitError') }}
                </h4>
                  
                <p class="sdds-message-extended">
                  {{ contactRequestErrorFields.map(field => $i18n(field)).join(', ') }}
                </p>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <p class="thank-you-message">
            {{ $i18n('orderRequestConfirmation') || 'Thank you for contacting us. We will get back to you as soon as possible.' }}
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .sdds-modal-header, .sdds-modal-actions {
    justify-content: space-between;
  }
  .sdds-modal-header > h3 {
    margin: 0;
  }
  .sdds-modal-body > * {
    padding-bottom: var(--sdds-spacing-layout-24);
  }
  .sdds-modal-actions {
    display: flex;
  }
  .sdds-modal {
    padding-bottom: var(--sdds-spacing-layout-24);
}

sdds-textarea {
  --sdds-textfield-bg: var(--sdds-white);
}

  .option-divider {
    padding-bottom: unset;
    margin-top: var(--sdds-spacing-layout-24);
    margin-bottom: var(--sdds-spacing-layout-24);
    background-color: var(--sdds-grey-300);
    height: 0.0625rem;
    border: none;
  }
  
  .divider {
    border: none;
    height: 0.0625rem;
    background-color: var(--sdds-grey-200);
    margin: var(--sdds-spacing-layout-24) 0;
  }

  .get-contacted-section {
    overflow: hidden;
  }
  
  .get-contacted-header {
    padding: var(--sdds-spacing-layout-16);
    background-color: var(--sdds-white);
    border: 0.0625rem solid var(--sdds-grey-300);
    border-radius: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
  z-index: 1;
  }
  .get-contacted-header:has(+ .get-contacted-content.expanded) {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom-color: transparent;
}
  
  .get-contacted-title {
    display: flex;
    align-items: center;
    color: var(--sdds-grey-900);
  }
  
  .accordion-icon {
    display: inline-flex;
    margin-right: var(--sdds-spacing-element-12);
    transition: transform 0.3s ease;
    color: var(--sdds-grey-900);
  }
  
  .accordion-icon img {
    width: 1.6em;
    height: 1.6em;
  }
  
  .accordion-icon.expanded {
    transform: rotate(180deg);
  }
  .get-contacted-content {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.5s ease-in-out, padding 0.5s ease-in-out, border 0s linear 0.5s;
    padding: 0 var(--sdds-spacing-layout-16);
    border-left: 0.0625rem solid transparent;
    border-right: 0.0625rem solid transparent;
    border-bottom: 0.0625rem solid transparent;
    border-radius: 0 0 0.25rem 0.25rem;
  }
  
  .get-contacted-content.expanded {
    max-height: 187.5em;
    padding: var(--sdds-spacing-layout-24) var(--sdds-spacing-layout-16) var(--sdds-spacing-layout-16) var(--sdds-spacing-layout-16);
    border-left: 0.0625rem solid var(--sdds-grey-300);
    border-right: 0.0625rem solid var(--sdds-grey-300);
    border-bottom: 0.0625rem solid var(--sdds-grey-300);
    margin-top: -0.0625rem;
    transition: max-height 0.5s ease-in-out, padding 0.5s ease-in-out, border 0s linear 0s;
  }.customer-input-textfield-bg {
    background-color: var(--sdds-white) !important;
}

.action-button-container {
  margin-top: var(--sdds-spacing-layout-24);
  display: flex;
  justify-content: flex-start;
}
label {
  font-family: "Scania Sans Semi Condensed", "Scania Sans Condensed", Arial, Helvetica, sans-serif;
  font-size: 0.89em;
  line-height: 1.9em;
  display: block;
  margin-bottom: var(--sdds-spacing-element-8);
  color: var(--sdds-textfield-label-color);
  font-weight: normal;
}
label span[style*="color: red;"] {
    color: #E31C1B !important;
    padding-left: 0.125em;
}
  .form-field {
    margin-top: var(--sdds-spacing-element-16);
    margin-bottom: 1em;
    min-width: 13em;
  }
  
  .required {
    color: red;
    margin-left: 0.125em;
  }
  
  .consent-section {
    margin-bottom: var(--sdds-spacing-element-12);
  }
  
  .consent-section h4 {
    font-size: 1em;
    font-weight: 500;
    margin-bottom: var(--sdds-spacing-element-12);
    color: var(--sdds-grey-900);
  }
  
  .consent-container {
    border: none;
    padding: 0;
  }
  .sdds-form-label {
    font-size: 0.89em;
    line-height: 1.2em;
    color: var(--sdds-grey-900);
  }
   
  .consent-notice {
    margin-bottom: var(--sdds-spacing-layout-24);
    margin-top: var(--sdds-spacing-element-16);
  }
  
  .thank-you-message {
    font-size: 1em;
  line-height: 1.5;
  text-align: left;
  padding: 0;
  margin: 0;
  color: var(--sdds-grey-900);
  }

  .margin-bottom-24 {
    margin-bottom: var(--sdds-spacing-layout-24);
  }

  @media only screen and (max-width: 768px) {
    .get-contacted-content.expanded {
      padding: 0 var(--sdds-spacing-element-12) var(--sdds-spacing-element-12);
    }
    
    .consent-section h4 {
      font-size: 0.89em;
    }
    .get-contacted-header {
      margin-bottom: var(--sdds-spacing-element-4);
    }
  }

  .marketing-consent-container {
    margin-top: var(--sdds-spacing-layout-24);
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .consent-sections {
    display: flex;
    flex-direction: column;
    gap: var(--sdds-spacing-element-16);
    width: 100%;
  }

  .consent-section {
    border: 0.063em solid var(--sdds-grey-200);
  border-radius: 0.25em;
  padding: var(--sdds-spacing-element-12) var(--sdds-spacing-layout-16);
  margin-bottom: var(--sdds-spacing-element-12);
  }

  .sdds-headline-07 {
    margin-bottom: var(--sdds-spacing-element-12);
    font-size: 1em;
  }

  .consent-footer {
    margin-top: var(--sdds-spacing-element-8);
  }

  .consent-footer-links {
    margin-top: var(--sdds-spacing-element-8);
    margin-bottom: var(--sdds-spacing-element-24);
  }

  .sdds-checkbox-item {
    width: 100%;
  }

  .sdds-form-label {
    font-size: 0.89em;
    line-height: 1.2500em;
    color: var(--sdds-grey-900);
    white-space: normal;
    word-wrap: break-word;
  }
</style>
